import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../catalyst/dialog'
import { Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Heading } from '../catalyst/heading'
import { Input } from '../catalyst/input'
import { Link } from '../catalyst/link'
import { Select } from '../catalyst/select'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import { useFarms } from '../lib/farms'
import { useField } from '../lib/field'
import { useFertilizerLogs, type CreateFertilizerLogInput } from '../lib/fertilizer-logs'
import { ComplianceBadge } from '../lib/compliance-badge'
import { Shell } from '../pages/shell'
import type { FertilizerLog } from '../lib/types'
import { canadaGapBadgeProps, draftNumber, exportH2, issueFor, useCanadaGapStatus, useEntryCheck } from '../lib/canadagap'
import { useFieldSeason } from '../lib/field-seasons'
import { checkBadgeProps, EntryCheckPanel, InputWarning, SavedEntryNotice } from './entry-check'
import { EntryIssues } from './entry-issues'
import { SeasonHeaderDialog } from './season-header-dialog'

const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

function FertilizerLogDialog({
  open,
  onClose,
  initial,
  onSave,
}: {
  open: boolean
  onClose: () => void
  initial?: FertilizerLog
  onSave: (input: Omit<CreateFertilizerLogInput, 'fieldId' | 'seasonYear'>) => Promise<unknown>
}) {
  const [appliedOn, setAppliedOn] = useState(initial?.appliedOn ?? '')
  const [blend, setBlend] = useState(initial?.blend ?? '')
  const [rateAmount, setRateAmount] = useState(initial?.rateAmount == null ? '' : String(initial.rateAmount))
  const [rateUnit, setRateUnit] = useState(initial?.rateUnit ?? 'kg/ha')
  const [lotNumber, setLotNumber] = useState(initial?.lotNumber ?? '')
  const [applicatorName, setApplicatorName] = useState(initial?.applicatorName ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { org } = useAuth()
  const { farmId } = useParams<{ farmId: string }>()
  const check = useEntryCheck(org?.id, farmId, {
    form: 'h2Fertilizer',
    entry: { blend, rateAmount: draftNumber(rateAmount), rateUnit: rateAmount ? rateUnit : null, applicatorName },
  })

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!appliedOn || !blend.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave({
        appliedOn,
        blend: blend.trim(),
        rateAmount: rateAmount ? Number(rateAmount) : null,
        rateUnit: rateAmount && rateUnit ? rateUnit : null,
        lotNumber: lotNumber.trim() || null,
        applicatorName: applicatorName.trim() || null,
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save fertilizer log.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{initial ? 'Edit fertilizer application' : 'Log a fertilizer application'}</DialogTitle>
      <div className="flex flex-wrap gap-1.5">
        <ComplianceBadge framework="CanadaGAP" formCode="H2" formName="Agronomic Inputs (Other)" section="Section 1 — Fertilizer" {...checkBadgeProps(check)} />
      </div>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          <FieldGroup>
            <FormField>
              <Label>Date applied</Label>
              <Input type="date" value={appliedOn} onChange={(e) => setAppliedOn(e.target.value)} required autoFocus />
            </FormField>
            <FormField>
              <Label>Fertilizer blend</Label>
              <Input
                value={blend}
                onChange={(e) => setBlend(e.target.value)}
                placeholder="e.g. 20-20-20, Urea 46-0-0"
                required
              />
              <InputWarning result={check} input="blend" />
            </FormField>
            <div className="flex gap-3">
              <FormField className="flex-1">
                <Label>Rate applied</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={rateAmount}
                  onChange={(e) => setRateAmount(e.target.value)}
                  placeholder="0"
                />
                <InputWarning result={check} input="rateAmount" />
              </FormField>
              <FormField className="w-32">
                <Label>Unit</Label>
                <Select value={rateUnit} onChange={(e) => setRateUnit(e.target.value)}>
                  <option value="kg/ha">kg/ha</option>
                  <option value="L/ha">L/ha</option>
                  <option value="kg/ac">kg/ac</option>
                  <option value="L/ac">L/ac</option>
                  <option value="lb/ac">lb/ac</option>
                </Select>
              </FormField>
            </div>
            <FormField>
              <Label>Lot number</Label>
              <Input value={lotNumber} onChange={(e) => setLotNumber(e.target.value)} placeholder="If applicable" />
            </FormField>
            <FormField>
              <Label>Applicator name</Label>
              <Input value={applicatorName} onChange={(e) => setApplicatorName(e.target.value)} />
              <InputWarning result={check} input="applicatorName" />
            </FormField>
          </FieldGroup>
          <EntryCheckPanel result={check} formCode="H2" />
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <ComplianceBadge framework="CanadaGAP" formCode="H2" formName="Agronomic Inputs (Other)" section="Section 1 — Fertilizer" placement="above" {...checkBadgeProps(check)} />
          </div>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export function FertilizerLogsPage() {
  const { farmId, fieldId } = useParams<{ farmId: string; fieldId: string }>()
  const { org } = useAuth()
  const { getFarm } = useFarms()
  const currentYear = new Date().getFullYear()
  const [seasonYear, setSeasonYear] = useState(currentYear)
  const [dialog, setDialog] = useState<{ log?: FertilizerLog } | null>(null)
  const [seasonOpen, setSeasonOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  const { field, loading: fieldLoading, error: fieldError } = useField(org?.id, farmId, fieldId)
  const { logs, loading: logsLoading, error: logsError, addLog, updateLog } = useFertilizerLogs(org?.id, farmId, fieldId, seasonYear)
  const { status, reload: reloadStatus } = useCanadaGapStatus(org?.id, farmId, fieldId, seasonYear)
  const { season, saveSeason } = useFieldSeason(org?.id, farmId, fieldId, seasonYear)

  const farm = farmId ? getFarm(farmId) : undefined
  const backHref = `/farms/${farmId}/fields/${fieldId}`
  const loading = fieldLoading || logsLoading
  const error = fieldError || logsError

  const years = [currentYear - 2, currentYear - 1, currentYear]

  async function handleExport() {
    if (!org?.id || !farmId || !fieldId) return
    setExporting(true)
    setExportError(null)
    try {
      await exportH2(org.id, farmId, fieldId, seasonYear)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed.')
    } finally {
      setExporting(false)
    }
  }

  async function saveLog(input: Omit<CreateFertilizerLogInput, 'fieldId' | 'seasonYear'>) {
    if (!fieldId) return
    const log = dialog?.log ? await updateLog(dialog.log.id, input) : await addLog({ fieldId, seasonYear, ...input })
    await reloadStatus()
    if (log) setSaved(log.id)
  }

  // The after-save notice is about one entry: clear it when another dialog opens or the season changes.
  useEffect(() => {
    if (dialog) setSaved(null)
  }, [dialog])
  useEffect(() => setSaved(null), [seasonYear])

  return (
    <Shell>
      <Link href={backHref} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        {field?.name ?? farm?.name ?? 'Field'}
      </Link>

      {error ? (
        <div className="mt-6 rounded-lg border border-dashed border-red-300 p-10 text-center dark:border-red-900">
          <Text>{error}</Text>
        </div>
      ) : loading ? (
        <div className="mt-6 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
          <Text>Loading…</Text>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
            <div>
              <Heading>{field?.name} — Fertilizer logs</Heading>
              <div className="flex flex-wrap gap-1.5">
                <ComplianceBadge framework="CanadaGAP" formCode="H2" formName="Agronomic Inputs (Other)" section="Section 1 — Fertilizer" {...canadaGapBadgeProps(status, 'h2Fertilizer')} />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={seasonYear} onChange={(e) => setSeasonYear(Number(e.target.value))} className="w-28">
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
              <Button outline onClick={() => setSeasonOpen(true)}>
                Season details
              </Button>
              <Button outline onClick={handleExport} disabled={exporting || logs.length === 0}>
                {exporting ? 'Exporting…' : 'Export H2'}
              </Button>
              <Button href={`/farms/${farmId}/log/fertilizer?field=${fieldId}`}>Log fertilizer</Button>
            </div>
          </div>

          {exportError && <Text className="mt-3 text-red-600 dark:text-red-400">{exportError}</Text>}
          {saved && status && (
            <SavedEntryNotice formCode="H2" issues={[issueFor(status.forms.h2Fertilizer, saved)].filter((i) => i !== undefined)} onDismiss={() => setSaved(null)} />
          )}

          {logs.length === 0 ? (
            <div className="mt-8 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
              <Text>No fertilizer logs for {seasonYear}.</Text>
            </div>
          ) : (
            <ul className="mt-8 -mb-8">
              {logs.map((log, index) => (
                <li key={log.id}>
                  <div className="relative pb-8">
                    {index < logs.length - 1 && (
                      <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-zinc-200 dark:bg-zinc-700" aria-hidden="true" />
                    )}
                    <div className="relative flex gap-4">
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-zinc-100 ring-1 ring-zinc-950/10 dark:bg-zinc-800 dark:ring-white/10">
                        <span className="size-2 rounded-full bg-zinc-400 dark:bg-zinc-500" />
                      </div>
                      <button
                        type="button"
                        onClick={() => setDialog({ log })}
                        className="-m-2 min-w-0 flex-1 rounded-lg p-2 pt-2.5 text-left hover:bg-zinc-950/[2.5%] dark:hover:bg-white/[2.5%]"
                      >
                        <p className="text-sm font-semibold text-zinc-950 dark:text-white">{formatDate(log.appliedOn)}</p>
                        <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">{log.blend}</p>
                        <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
                          {log.rateAmount != null && log.rateUnit && <>{log.rateAmount} {log.rateUnit}</>}
                          {log.applicatorName && <>{log.rateAmount != null ? ' · ' : ''}{log.applicatorName}</>}
                          {log.lotNumber && <> · Lot {log.lotNumber}</>}
                        </p>
                        {(log.applicationMethod || log.equipmentName) && (
                          <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
                            {[
                              log.applicationMethod === 'drip' ? 'Fertigation' : log.applicationMethod && log.applicationMethod.charAt(0).toUpperCase() + log.applicationMethod.slice(1),
                              log.durationMinutes != null && `${log.durationMinutes} min`,
                              log.equipmentName,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        )}
                        <EntryIssues issue={issueFor(status?.forms.h2Fertilizer, log.id)} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {dialog && (
            <FertilizerLogDialog
              key={dialog.log?.id ?? 'new'}
              open
              onClose={() => setDialog(null)}
              initial={dialog.log}
              onSave={saveLog}
            />
          )}
          <SeasonHeaderDialog
            open={seasonOpen}
            onClose={() => setSeasonOpen(false)}
            seasonYear={seasonYear}
            season={season}
            saveSeason={async (input) => {
              await saveSeason(input)
              await reloadStatus()
            }}
          />
        </>
      )}
    </Shell>
  )
}
